import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from "@progress/kendo-react-common";
import { Window } from '@progress/kendo-react-dialogs';
import {
    ListItemProps, MultiSelect,
    MultiSelectChangeEvent,
    TagData
} from '@progress/kendo-react-dropdowns';
import { Field, Form, FormElement, FormRenderProps } from '@progress/kendo-react-form';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import { Input, MaskedTextBox } from '@progress/kendo-react-inputs';
import { searchIcon } from '@progress/kendo-svg-icons';
import React, { useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import CopyCustomerDialog from 'views/Customers/CopyCustomerDialog';
import { fetchApi } from '../services/api';
import { openWindowPerc } from '../services/openWindow';
import PhoneCell from './cells/PhoneCell';

export type CustomerInputValue = {
  CustomerID: number;
  CustomerNumber: string;
  CustomerName?: string;
  City?: string;
  State?: string;
  ZipCode?: string;
  AddressLine1?: string;
};

type CustomerAutocomplete = {
  CustomerID: number;
  CustomerNumber: string;
  CustomerName: string;
  AddressLine1: string;
  CityName: string;
  State: string;
  ZipCode: string;
};

type CustomerSearchValue = CustomerAutocomplete & {
  PhoneNumber: string;
  PhoneNumberExtension: string;
};

type Props = {
  placeholder?: string;
  value: CustomerInputValue[];
  onChange: (response: CustomerInputValue[]) => void;
  onCreditHold?: boolean;
  disabled?: boolean;
  StopNote?: string;
  City?: string;
  State?: string;
  ZipCode?: string;
  fullWidth?: boolean;
};

const MultipleCustomerInput: React.FC<Props> = (props) => {
  const multiselectRef = useRef<MultiSelect>(null);
  const _requestHash = useRef('');
  const [data, setData] = useState<CustomerAutocomplete[]>([]);
  const [isLoadingAutoComplete, setIsLoadingAutoComplete] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [customerSearch, setCustomerSearch] = useState(false);
  const [searchData, setSearchData] = useState<CustomerSearchValue[]>([]);
  const [isLoadingSearchData, setIsLoadingSearchData] = useState(false);
  const [showCopyCustomerDialog, setShowCopyCustomerDialog] = useState(false);
  const searchFormRef = useRef<any>(null);
  const [customerSearchData, setCustomerSearchData] = useState({
    CustomerName: '',
    City: props.City || '',
    State: props.State || '',
    AddressLine1: '',
    PhoneNumber: '',
    Country: '',
    ZipCode: props.ZipCode || '',
    CustomerNumber: '',
  });

  const value = props.value || [];

  const search = useDebouncedCallback((term: string, requestHash: string) => {
    setIsLoadingAutoComplete(true);
    fetchApi("/api/Customer/Autocomplete", { Term: term.trim(), OnCreditHold: props.onCreditHold }, "POST")
      .then((options: CustomerAutocomplete[]) => {
        if (_requestHash.current === requestHash) {
          setData(options);
          setIsLoadingAutoComplete(false);
          setIsDropdownOpen(options?.length > 0);
        }
      });
  }, 750);

  const convertToInputValue = (c: CustomerAutocomplete): CustomerInputValue => ({
    CustomerID: c.CustomerID,
    CustomerNumber: c.CustomerNumber,
    CustomerName: c.CustomerName,
    City: c.CityName,
    State: c.State,
    ZipCode: c.ZipCode,
    AddressLine1: c.AddressLine1,
  });

  const searchCustomers = (values: { [name: string]: any }) => {
    setIsLoadingSearchData(true);

    fetchApi("/api/Customer/Search", { ...values, OnCreditHold: props.onCreditHold }, "POST")
      .then((res: { Customers: CustomerSearchValue[] }) => {
        setSearchData(res.Customers);
        setIsLoadingSearchData(false);
      });
  };

  const itemRenderCustomer = (li: React.ReactElement<HTMLLIElement>, itemProps: ListItemProps) => (
    React.cloneElement(li, li.props, (
      <span>
        {itemProps.dataItem.CustomerNumber} - {itemProps.dataItem.CustomerName} - {itemProps.dataItem.CityName}, {itemProps.dataItem.State} {itemProps.dataItem.ZipCode}
      </span>
    ))
  );

  const customerSearchWindow = () => (
    <Window
      title="Customer Search"
      onClose={() => { setCustomerSearch(false); setSearchData([]); }}
      initialWidth={Math.min(850, window.innerWidth)}
      initialHeight={Math.min(550, window.innerHeight)}
    >
      {showCopyCustomerDialog && (
        <CopyCustomerDialog
          StopNote={props.StopNote}
          City={props.City}
          State={props.State}
          ZipCode={props.ZipCode}
          CloseDialog={(newCustomerId: number) => {
            if (newCustomerId > 0) {
              fetchApi("/api/Customer/Search", { CustomerID: newCustomerId }, "POST")
                .then((res: { Customers: CustomerSearchValue[] }) => {
                  props.onChange([...value, convertToInputValue(res.Customers[0])]);
                });
            } else {
              setShowCopyCustomerDialog(false);
            }
          }}
        />
      )}

      {props.StopNote && <div className="alert alert-info" role="alert">{props.StopNote}</div>}

      <Form
        initialValues={customerSearchData}
        onSubmit={searchCustomers}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <div className="row">
              <div className="col-md-4"><Field name="CustomerName" component={Input} label="Name" /></div>
              <div className="col-md-4"><Field name="City" component={Input} label="City" /></div>
              <div className="col-md-4"><Field name="State" component={Input} label="State" maxLength={2} /></div>
            </div>
            <div className="row">
              <div className="col-md-4"><Field name="AddressLine1" component={Input} label="Address" /></div>
              <div className="col-md-4"><Field name="PhoneNumber" component={MaskedTextBox} label="Phone" mask="(000) 000-0000" /></div>
              <div className="col-md-4"><Field name="Country" component={Input} label="Country" /></div>
            </div>
            <div className="row">
              <div className="col-md-4"><Field name="ZipCode" component={Input} label="Zip Code" /></div>
              <div className="col-md-4"><Field name="CustomerNumber" component={Input} label="My Code" /></div>
            </div>
            <br />
            <div className="text-center">
              <Button themeColor="primary" disabled={!formRenderProps.allowSubmit}>Search</Button>
              &nbsp;&nbsp;
              <Button themeColor="primary" onClick={() => setShowCopyCustomerDialog(true)}>Add Customer</Button>
              &nbsp;&nbsp;
              <Button type="button" onClick={() => {
                setCustomerSearchData({
                  CustomerName: '',
                  City: '',
                  State: '',
                  AddressLine1: '',
                  PhoneNumber: '',
                  Country: '',
                  ZipCode: '',
                  CustomerNumber: '',
                });
                searchFormRef.current?.resetForm?.();
              }}>Reset</Button>
            </div>
          </FormElement>
        )}
      />
      <br />
      {isLoadingSearchData && <span className="k-i-loading k-icon" style={{ fontSize: 64, position: 'absolute', left: '50%', top: '5%' }} />}
      <Grid
        data={searchData}
        dataItemKey="CustomerID"
        onRowClick={(e) => {
          const selected = convertToInputValue(e.dataItem);
          if (!value?.some(v => v.CustomerID === selected.CustomerID)) {
            props.onChange([...value, selected]);
          }
          setCustomerSearch(false);
        }}
      >
        <Column field="CustomerNumber" title="My Code" />
        <Column field="CustomerName" title="Name" />
        <Column field="AddressLine1" title="Address" />
        <Column field="City" />
        <Column field="State" />
        <Column field="ZipCode" title="Zip" />
        <Column field="PhoneNumber" title="Phone" cells={{ data: PhoneCell }} />
      </Grid>
    </Window>
  );

  const openCustomer = (customerId: number) => {
    openWindowPerc(`/Customers/Customer/${customerId}`, .90, .90, 'CustomerInput');
  }

  const tagRender = (tagData: TagData, tagElement: React.ReactElement) => {
    const item = tagData.data[0] as CustomerInputValue;

    return (
      <span key={tagElement.key} title={`${item.CustomerName} - ${item.City} ${item.State}, ${item.ZipCode}`}>
        {React.cloneElement(tagElement, {
          onClick: (e: any) => {
            const target = e?.syntheticEvent?.target as HTMLElement;
            if (!target.innerText) return;

            if (e.syntheticEvent?.stopPropagation) {
              e.syntheticEvent.stopPropagation();
            }

            openCustomer(item.CustomerID);
          },
        })}
      </span>
    );
  };

  return (
    <>
      {customerSearch && customerSearchWindow()}
      <div className={props.fullWidth ? 'd-flex w-100' : undefined}>
        <MultiSelect
          opened={isDropdownOpen}
          onBlur={() => setIsDropdownOpen(false)}
          style={{
            ...(props.fullWidth ? { flex: 1 } : { width: 300 }),
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            borderRight: 'none'
          }}
          ref={multiselectRef}
          data={data}
          textField="CustomerNumber"
          dataItemKey="CustomerID"
          filterable
          loading={isLoadingAutoComplete}
          placeholder={props.placeholder || 'Customers'}
          itemRender={itemRenderCustomer}
          disabled={props.disabled ?? false}
          value={props.value}
          tagRender={tagRender}
          summarizeAfter={Infinity}
          onChange={(event: MultiSelectChangeEvent) => {
            const selected = event.value as CustomerAutocomplete[];
            props.onChange(selected.map(convertToInputValue));
            setData([]);
          }}
          onFilterChange={(e) => {
            const val = e.filter?.value;
            if (val && val.length > 2) {
              _requestHash.current = Math.random().toString(36).substring(7);
              search(val, _requestHash.current);
            } else
              setIsDropdownOpen(false);
          }}
        />
        <button
          className="btn btn-outline-secondary"
          type="button"
          onClick={() => setCustomerSearch(true)}
          style={{
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            borderLeft: 'none',
          }}
        >
          <SvgIcon icon={searchIcon} />
        </button>
      </div>
    </>
  );
};

export default MultipleCustomerInput;
